/* Measured geometry for every Steffi render used on the launch page.
 *
 * Produced by the asset pipeline: each photograph ships on a pure-white
 * seamless backdrop with its display reconstructed as blank glass, plus the
 * exact frame the animated face has to be drawn in.
 *
 *   face.cx / face.cy   centre of the display, in % of the image box
 *   face.rw / face.rh   the display's equal-area radius, in % of width / height
 *   face.transform      how a head-on face maps onto that render's perspective
 *   outline             the true elliptical outline of the glass
 */
import { useCallback } from "react";
import { usePublicLang } from "../../config/i18n/public";

/* The current hardware, from the front, cut out on a transparent ground with
   a blank display, so the animated face and the on-screen UI are drawn on it. Each
   crop frames the bot the way the earlier render of that slot did, so it
   appears at the same size on the page. */
const FRONT = {
  src: "/steffi/bot-render-front.webp",
  w: 780,
  h: 1108,
  face: { cx: 50.321, cy: 30.821, rw: 33.462, rh: 23.556, transform: "" },
  outline: { rx: 33.462, ry: 23.556, angle: 0 },
  // where the base meets the floor, in % of the image box, and how wide it is
  ground: { y: 95.2, w: 66 },
};

export const RENDERS = {
  // front, awake — the hero, the speak chapter and the finale
  front: FRONT,
  // the activation chapter and the reminders (once a forest colourway)
  green: FRONT,
  // the video-call chapter: framed wider, like the earlier calm render
  calm: {
    src: "/steffi/bot-render-calm.webp",
    w: 1000,
    h: 1132,
    face: { cx: 50.281, cy: 28.223, rw: 24.415, rh: 21.57, transform: "" },
    outline: { rx: 24.415, ry: 21.57, angle: 0 },
    ground: { y: 87.4, w: 50 },
  },
  // three-quarter — the "speak naturally" chapter
  side: {
    src: "/steffi/steffi-side.webp",
    w: 523,
    h: 780,
    face: {
      cx: 38.769,
      cy: 32.241,
      rw: 32.909,
      rh: 22.066,
      transform: "rotate(1.04deg) skewX(-4.12deg) scale(0.996, 1.078)",
    },
    outline: { rx: 36.494, ry: 20.585, angle: -54.1 },
  },
  // rear three-quarter — the hardware detail
  back: { src: "/steffi/steffi-back.webp", w: 820, h: 1025 },
};

/* The round screens Steffi shows when a reminder lands. Square crops centred
   on the disc map onto the round glass 1:1. */
export const SCREENS = {
  oven: "/steffi/screens/oven.webp",
  timer: "/steffi/screens/timer.webp",
  drink: "/steffi/screens/drink.webp",
  activity: "/steffi/screens/activity.webp",
  medication: "/steffi/screens/medication.webp",
  familyFeed: "/steffi/screens/family-feed.webp",
  weather: "/steffi/screens/weather.webp",
  exercise: "/steffi/screens/exercise.webp",
  wellbeingFace: "/steffi/screens/wellbeing-face.webp",
  musicLeft: "/steffi/screens/music-left.webp",
  musicRight: "/steffi/screens/music-right.webp",
};

/* Dashboards for the Wohlbefinden & Sicherheit chapters. The emergency screen
   is not shot yet — its slot shows a placeholder until the file exists. */
export const WELLBEING_SHOTS = {
  emergency: { src: "/steffi/app/emergency.webp", w: 876, h: 1796 },
  participation: { src: "/steffi/app/participation.webp", w: 820, h: 1714 },
  biomarker: { src: "/steffi/app/biomarker.webp", w: 820, h: 1765 },
};

/* Companion-app screens, exported with their iPhone frame intact. */
export const APP_SHOTS = {
  feed: { src: "/steffi/app/feed.webp", w: 920, h: 1810 },
  calendar: { src: "/steffi/app/calendar.webp", w: 820, h: 1651 },
  reminders: { src: "/steffi/app/reminders.webp", w: 914, h: 1800 },
  routines: { src: "/steffi/app/routines.webp", w: 820, h: 1599 },
  game: { src: "/steffi/app/game.webp", w: 820, h: 1561 },
  activationRoutines: { src: "/steffi/app/activation-routines.webp", w: 903, h: 1742 },
  activationCheck: { src: "/steffi/app/activation-check.webp", w: 898, h: 1800 },
  profile: { src: "/steffi/app/profile.webp", w: 820, h: 1649 },
};

/* The English screenshots were generated from the German ones and cropped back
   to the very same pixel dimensions, so only the path changes per language:
   /steffi/app/feed.webp → /steffi/app/en/feed.webp. */
const LOCALIZED_DIRS = ["/steffi/screens/", "/steffi/app/"];

export function localizeAsset(src, lang) {
  if (lang !== "en" || typeof src !== "string") return src;
  if (!LOCALIZED_DIRS.some((dir) => src.startsWith(dir))) return src;
  const cut = src.lastIndexOf("/");
  return `${src.slice(0, cut)}/en${src.slice(cut)}`;
}

/* Returns a mapper for the current language: `l(SCREENS.oven)`, or
   `l(APP_SHOTS.feed)` for a {src,w,h} record. */
export function useLocalizedAsset() {
  const { lang } = usePublicLang();
  return useCallback(
    (asset) =>
      typeof asset === "string"
        ? localizeAsset(asset, lang)
        : { ...asset, src: localizeAsset(asset.src, lang) },
    [lang]
  );
}

/* Lifestyle plates. `table` is the real render composited into the scene;
   `living` was generated with the studio render supplied as the reference, so
   the product in frame is the real Steffi. */
export const SCENES = {
  living: { src: "/steffi/scene/living.webp", w: 2000, h: 1333 },
  emergencyHelp: { src: "/steffi/scene/emergency-help.webp", w: 1600, h: 1600 },
  call: { src: "/steffi/scene/call-video.webp", w: 2000, h: 851 },
  table: { src: "/steffi/scene/table.webp", w: 1376, h: 768 },
  hands: { src: "/steffi/scene/hands.webp", w: 1376, h: 768 },
  room: { src: "/steffi/scene/room.webp", w: 1376, h: 768 },
};
